import { ALGN_VER3_MAP } from '@src/slide/domain/ui-model/converter/node-property/sdk-ui-enum-map/algn';
import {
    EEffectAlgn, IEffectModelValCollection, IShadowModelVal,
} from '@src/slide/domain/ui-model/element-modeles/property/effect/effect.interface';
import {
    alphaToEmu, alphaToOpacity, angleToEmu, emuToAlpha, emuToAngle,
    emuToPercent, emuToPt, opacityToAlpha, percentToEmu, ptToEmu,
} from '@src/slide/infrastructure/module/independent/unit';
import { DeltaOf, ICTEffectList, ICTInnerShadowEffect, ICTOuterShadowEffect, IEGColorChoice, STRectAlignment } from '@tencent/slide-sdk-model';
import { DeepPartial, DeepReadonly } from 'ts-essentials';
import { invertMap } from '@src/slide/infrastructure/module/independent/object/map-invert';
import _set from 'lodash/set';
import { ITDColor, TDColor } from '@src/slide/domain/ui-model/element-modeles/property/color/td-color';
import {
    ColorConverter,
} from '@src/slide/domain/ui-model/converter/node-property/module-property/color/color-converter';

/**
 * 效果 - 阴影
 */
export class ShadowConverter {
    /**
     * 阴影的sdk -> ui 映射
     * @param sdkData
     * @param uiData
     */
    public static convertPartialDataFromSdkToUi(
        sdkData: DeepReadonly<DeepPartial<ICTEffectList>>,
        uiData: IEffectModelValCollection,
    ): void {
        const isOuter = ShadowConverter.judgeIsOuter(sdkData.outerShdw);
        const sdkShadow = isOuter ? sdkData.outerShdw : sdkData.innerShdw;
        if (sdkShadow) {
            const algn = ShadowConverter.convertAlgnFromSdkToUi(sdkData.outerShdw);
            const blurRad = ShadowConverter.convertBlurRadFromSdkToUi(sdkShadow);
            const dir = ShadowConverter.convertDirFromSdkToUi(sdkShadow);
            const dist = ShadowConverter.convertDistFromSdkToUi(sdkShadow);
            const rotWithShape = ShadowConverter.convertRotWithShapeFromSdkToUi(sdkData.outerShdw);
            const { color, opacity } = ShadowConverter.convertColorFromSdkToUi(sdkShadow);
            const { size, sizeX, sizeY } = ShadowConverter.convertSizeFromSdkToUi(sdkShadow);
            const { skewX, skewY } = ShadowConverter.convertSkewFromSdkToUi(sdkShadow);
            const egColorChoice = sdkShadow.egColorChoice as DeepReadonly<DeepPartial<IEGColorChoice>>;
            const tdColor = ColorConverter.convertPartialDataFromSdkToUi(egColorChoice);

            uiData.shadow = {
                isOuter,
                algn,
                blurRad,
                dir,
                dist,
                rotWithShape,
                color,
                tdColor,
                opacity,
                size,
                sizeX,
                sizeY,
                skewX,
                skewY,
            };
            // 清掉值是undefined的属性
            Object.keys(uiData.shadow).forEach((p) => {
                if (uiData.shadow && uiData.shadow[p] === undefined) {
                    delete uiData.shadow[p];
                }
            });
        } else {
            delete uiData.shadow;
        }
    }

    /**
     * 阴影的ui-> sdk 映射
     * @param uiData
     * @param sdkData
     */
    public static convertPartialDataFromUiToSdk(
        uiData: DeepPartial<IEffectModelValCollection>,
        sdkData: DeltaOf<ICTEffectList>,
    ): void {
        if (uiData.shadow) {
            const { sx, sy } = ShadowConverter.convertSizeFromUiToSdk(uiData.shadow);
            const { kx, ky } = ShadowConverter.convertSkewFromUiToSdk(uiData.shadow);
            const egColorChoice = {};
            const tdColor = uiData.shadow.tdColor as DeepReadonly<DeepPartial<ITDColor>>;
            ColorConverter.convertPartialDataFromUiToSdk(tdColor, egColorChoice);

            const Shdw = {
                algn: ShadowConverter.convertAlgnFromUiToSdk(uiData.shadow),
                blurRad: ShadowConverter.convertBlurRadFromUiToSdk(uiData.shadow),
                dir: ShadowConverter.convertDirFromUiToSdk(uiData.shadow),
                dist: ShadowConverter.convertDistFromUiToSdk(uiData.shadow),
                rotWithShape: ShadowConverter.convertRotWithShapeFromUiToSdk(uiData.shadow),
                egColorChoice,
                sx,
                sy,
                kx,
                ky,
            };
            // 清掉值是undefined的属性
            Object.keys(Shdw).forEach((p) => {
                if (Shdw && Shdw[p] === undefined) {
                    delete Shdw[p];
                }
            });
            if (uiData.shadow.isOuter) {
                sdkData.outerShdw = Shdw;
                sdkData.innerShdw = null;
            } else {
                sdkData.outerShdw = null;
                sdkData.innerShdw = Shdw;
            }
        } else if (uiData.shadow === null) {
            sdkData.outerShdw = null;
            sdkData.innerShdw = null;
        }
    }

    // 判断是否为外阴影
    private static judgeIsOuter(outerShdw?: DeepReadonly<DeepPartial<ICTOuterShadowEffect>>): boolean {
        if (!outerShdw) {
            return false;
        }
        return ['egColorChoice', 'blurRad', 'dist', 'dir', 'sx', 'sy', 'kx', 'ky', 'rotWithShape']
            .some(e => outerShdw[e] || outerShdw[e] === 0);
    }

    // algn
    private static convertAlgnFromSdkToUi(sdkData?:
    DeepReadonly<DeepPartial<ICTOuterShadowEffect>> | null): EEffectAlgn | undefined {
        if (sdkData?.algn) {
            return ALGN_VER3_MAP.get(sdkData.algn);
        }
        // 有负数sy但是没有algn是透视的一种场景，做下兼容
        if (sdkData?.sy && sdkData.sy < 0) {
            return EEffectAlgn.B;
        }
    }

    // blurRad
    private static convertBlurRadFromSdkToUi(sdkData?:
    DeepReadonly<DeepPartial<ICTOuterShadowEffect | ICTInnerShadowEffect>> | null): number {
        if (sdkData?.blurRad || sdkData?.blurRad === 0) {
            return emuToPt(sdkData.blurRad);
        }
        return 0;
    }

    // dir
    private static convertDirFromSdkToUi(sdkData?:
    DeepReadonly<DeepPartial<ICTOuterShadowEffect | ICTInnerShadowEffect>> | null): number {
        if (sdkData?.dir || sdkData?.dir === 0) {
            return emuToAngle(sdkData.dir);
        }
        return 0;
    }

    // dist
    private static convertDistFromSdkToUi(sdkData?:
    DeepReadonly<DeepPartial<ICTOuterShadowEffect | ICTInnerShadowEffect>> | null): number {
        if (sdkData?.dist || sdkData?.dist === 0) {
            return emuToPt(sdkData.dist);
        }
        return 0;
    }

    // rotWithShape
    private static convertRotWithShapeFromSdkToUi(sdkData?:
    DeepReadonly<DeepPartial<ICTOuterShadowEffect>> | null): number | undefined {
        if (sdkData?.rotWithShape === true || sdkData?.rotWithShape === false) {
            return sdkData.rotWithShape ? 1 : 0;
        }
    }

    // color/opacity
    private static convertColorFromSdkToUi(sdkData?:
    DeepReadonly<DeepPartial<ICTOuterShadowEffect>> | null): { color: string, opacity: number } {
        const rgbColor = sdkData?.egColorChoice?.srgbClr?.val;
        const alpha = sdkData?.egColorChoice?.srgbClr?.egColorTransform?.[0]?.alpha?.val;
        if (rgbColor) {
            return {
                color: rgbColor?.startsWith('#') ? rgbColor : `#${rgbColor}`,
                opacity: alpha === undefined ? 0 : alphaToOpacity(emuToAlpha(alpha)),
            };
        }
        return { color: '#000000', opacity: 0 };
    }

    // size/sizeX/sizeY
    private static convertSizeFromSdkToUi(sdkData?:
    DeepReadonly<DeepPartial<ICTOuterShadowEffect>> | null): { size: number, sizeX: number, sizeY: number } {
        let sizeX;
        let sizeY;
        if (sdkData?.sx || sdkData?.sx === 0) {
            sizeX = emuToPercent(sdkData.sx);
        }
        if (sdkData?.sy || sdkData?.sy === 0) {
            sizeY = emuToPercent(sdkData.sy);
        }
        let size = sizeX ?? sizeY ?? 100;
        if (size < 0) {
            size = 100;
        }
        return { size, sizeX: sizeX ?? 100, sizeY: sizeY ?? 100 };
    }

    // skewX/skewY
    private static convertSkewFromSdkToUi(sdkData?:
    DeepReadonly<DeepPartial<ICTOuterShadowEffect>> | null): { skewX?: number, skewY?: number } {
        return {
            skewX: (sdkData?.kx || sdkData?.kx === 0) ? emuToAngle(sdkData.kx) : undefined,
            skewY: (sdkData?.ky || sdkData?.ky === 0) ? emuToAngle(sdkData.ky) : undefined,
        };
    }

    // algn
    private static convertAlgnFromUiToSdk(uiData?:
    DeepPartial<IShadowModelVal> | null): STRectAlignment | undefined | null {
        if (uiData?.algn) {
            return invertMap(ALGN_VER3_MAP).get(uiData.algn);
        } if (uiData?.algn === null) {
            return null;
        }
    }

    // blurRad
    private static convertBlurRadFromUiToSdk(uiData?:
    DeepPartial<IShadowModelVal> | null): number | undefined {
        if (uiData?.blurRad || uiData?.blurRad === 0) {
            return ptToEmu(uiData.blurRad);
        }
    }

    // dir
    private static convertDirFromUiToSdk(uiData?:
    DeepPartial<IShadowModelVal> | null): number | undefined {
        if (uiData?.dir || uiData?.dir === 0) {
            return angleToEmu(uiData.dir);
        }
    }

    // dist
    private static convertDistFromUiToSdk(uiData?:
    DeepPartial<IShadowModelVal> | null): number | undefined {
        if (uiData?.dist || uiData?.dist === 0) {
            return ptToEmu(uiData.dist);
        }
    }

    // rotWithShape
    private static convertRotWithShapeFromUiToSdk(uiData?:
    DeepPartial<IShadowModelVal> | null): boolean | undefined {
        if (uiData?.rotWithShape === 1 || uiData?.rotWithShape === 0) {
            return !!uiData.rotWithShape;
        }
    }

    // color/opacity
    private static convertColorFromUiToSdk(uiData?:
    DeepPartial<IShadowModelVal> | null): IEGColorChoice | undefined {
        if (uiData?.color || (uiData?.opacity || uiData?.opacity === 0)) {
            const egColor: IEGColorChoice = {};
            _set(egColor, 'srgbClr.val', uiData.color?.replace(/#/, ''));
            _set(egColor, 'srgbClr.egColorTransform[0].alpha.val', alphaToEmu(opacityToAlpha(uiData.opacity ?? 0)));
            return egColor;
        }
    }

    // sizeX/sizeY
    private static convertSizeFromUiToSdk(uiData?:
    DeepPartial<IShadowModelVal> | null): { sx?: number, sy?: number } {
        const sizeX = uiData?.sizeX || uiData?.size;
        const sizeY = uiData?.sizeY || uiData?.size;
        let sx;
        let sy;
        if (sizeX || sizeX === 0) {
            sx = percentToEmu(sizeX);
        }
        if (sizeY || sizeY === 0) {
            sy = percentToEmu(sizeY);
        }
        return { sx, sy };
    }

    // skewX/skewY
    private static convertSkewFromUiToSdk(uiData?:
    DeepPartial<IShadowModelVal> | null): { kx?: number, ky?: number } {
        return {
            kx: (uiData?.skewX || uiData?.skewX === 0) ? angleToEmu(uiData.skewX) : undefined,
            ky: (uiData?.skewY || uiData?.skewY === 0) ? angleToEmu(uiData.skewY) : undefined,
        };
    }
}
